import { ServiceIndisponibleError, SessionExpireeError, getApi } from '@/lib/api';
import { lireSession } from '@/lib/session';
import type { NoyauxTexte } from '@rosetta/shared';
import { useQuery } from '@tanstack/react-query';

/** While the choice is open, the state of the noyaux is read again at this pace. */
export const INTERVALLE_NOYAUX_MS = 5000;

/** The query key of the noyaux of the text of a translation (#11). */
export const cleNoyauxTexte = (id: string) => ['noyaux-texte', id] as const;

/**
 * Where each noyau stands for the text of the translation `id` (#11), for the choice of the
 * noyaux to add: read when the choice opens (never an old answer: a noyau may have been added
 * since), again every 5 s while it is open, and a blip of the service is tried
 * once more. Reads only: nothing here writes in Tahiti.
 */
export function useNoyauxTexte(id: string) {
  return useQuery<NoyauxTexte, Error>({
    queryKey: cleNoyauxTexte(id),
    queryFn: () => {
      const session = lireSession();
      if (session === null) throw new SessionExpireeError();
      return getApi().lireNoyauxTexte(session, id);
    },
    staleTime: 0,
    gcTime: 0,
    retry: (essais, erreur) => erreur instanceof ServiceIndisponibleError && essais < 1,
    // Another tab or another device may add a noyau while the choice is open: it turns "en cours"
    // (N2) instead of staying free on an old answer.
    refetchInterval: (requete) => (requete.state.data === undefined ? false : INTERVALLE_NOYAUX_MS),
  });
}
